import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Sparkles, Loader2, Send, Trash2, RefreshCw, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const SECTIONS = [
  { key: 'duties', label: 'Duties & Responsibilities' },
  { key: 'event_briefing', label: 'Event Briefing' },
  { key: 'performance_expectations', label: 'Performance Expectations' },
  { key: 'reporting_requirements', label: 'Reporting Requirements' },
  { key: 'required_photos', label: 'Required Photos / Evidence' },
  { key: 'dress_code', label: 'Dress Code & Appearance' },
  { key: 'parking_logistics', label: 'Parking & Logistics' },
];

const asText = (v) => (Array.isArray(v) ? v.join('\n') : (v || ''));

const STATUS_META = {
  proposed: { label: 'AI Draft — review & submit', cls: 'bg-amber-100 text-amber-800', Icon: Sparkles },
  submitted: { label: 'Submitted — awaiting admin approval', cls: 'bg-blue-100 text-blue-800', Icon: Clock },
  approved: { label: 'Approved — visible to talent', cls: 'bg-green-100 text-green-800', Icon: CheckCircle2 },
  discarded: { label: 'Discarded', cls: 'bg-gray-100 text-gray-600', Icon: Trash2 },
};

/**
 * Creator-facing AI Requirement Organizer (client + agency).
 * basePath e.g. "/client/shifts/<id>" or "/agency/jobs/<id>".
 */
export const RequirementOrganizer = ({ basePath, entityLabel = 'job' }) => {
  const [draft, setDraft] = useState(null);
  const [sections, setSections] = useState({});
  const [checklist, setChecklist] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const hydrate = useCallback((d) => {
    setDraft(d);
    const s = (d && d.sections) || {};
    const next = {};
    SECTIONS.forEach(({ key }) => { next[key] = asText(s[key]); });
    setSections(next);
    setChecklist(asText(s.task_checklist));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${basePath}/requirement-draft`);
      hydrate(res.data && res.data.draft ? res.data.draft : null);
    } catch (e) {
      hydrate(null);
    } finally {
      setLoading(false);
    }
  }, [basePath, hydrate]);

  useEffect(() => { load(); }, [load]);

  const buildSections = () => {
    const out = {};
    SECTIONS.forEach(({ key }) => { out[key] = (sections[key] || '').trim(); });
    out.task_checklist = checklist.split('\n').map((l) => l.trim()).filter(Boolean);
    return out;
  };

  const isEmptySections = (obj) => {
    const vals = Object.values(obj || {});
    return !vals.some((v) => (Array.isArray(v) ? v.length : String(v || '').trim()));
  };

  const organize = async () => {
    setBusy(true);
    try {
      const res = await api.post(`${basePath}/organize-requirements`);
      hydrate({ ...res.data, status: res.data.status || 'proposed' });
      if (isEmptySections(res.data.sections)) {
        toast.info('No existing instructions found to organize. Add job details first, then try again.');
      } else {
        toast.success('AI organized your existing instructions. Review and edit before submitting.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not organize requirements.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const payload = buildSections();
    if (isEmptySections(payload)) {
      toast.error('Add at least one requirement before submitting for approval.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`${basePath}/requirement-draft/submit`, { sections: payload });
      toast.success('Submitted for administrator approval.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit for approval.');
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    try {
      await api.post(`${basePath}/requirement-draft/discard`);
      toast.success('Draft discarded.');
      hydrate(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not discard draft.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4" data-testid="organizer-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading requirement organizer…
      </div>
    );
  }

  const status = draft?.status;
  const meta = STATUS_META[status];

  return (
    <div className="rounded-xl border border-indigo-200 bg-white p-4 sm:p-5" data-testid="requirement-organizer">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-indigo-600" />
          <h3 className="text-base font-semibold text-gray-900">AI Requirement Organizer</h3>
        </div>
        {meta && (
          <Badge className={meta.cls} data-testid="organizer-status">
            <meta.Icon className="h-3.5 w-3.5 mr-1" /> {meta.label}
          </Badge>
        )}
      </div>

      <p className="text-xs text-gray-600 mb-4">
        AI only reorganizes the instructions you already wrote for this {entityLabel} — it never invents new
        requirements. Your original description is always kept. An administrator gives final approval before talent see it.
      </p>

      {!draft ? (
        <Button onClick={organize} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="organize-ai-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          Organize with AI
        </Button>
      ) : (
        <div className="space-y-4">
          {SECTIONS.map(({ key, label }) => (
            <div key={key} data-testid={`organizer-section-${key}`}>
              <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
              <Textarea
                value={sections[key] || ''}
                onChange={(e) => setSections((p) => ({ ...p, [key]: e.target.value }))}
                disabled={status === 'submitted'}
                rows={2}
                className="bg-white text-sm"
                data-testid={`organizer-input-${key}`}
                placeholder="—"
              />
            </div>
          ))}
          <div data-testid="organizer-section-task_checklist">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Task Checklist (one per line)</label>
            <Textarea
              value={checklist}
              onChange={(e) => setChecklist(e.target.value)}
              disabled={status === 'submitted'}
              rows={4}
              className="bg-white text-sm"
              data-testid="organizer-input-task_checklist"
              placeholder="One task per line"
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {status !== 'submitted' && (
              <Button onClick={submit} disabled={busy} className="bg-green-600 hover:bg-green-700 text-white" data-testid="organizer-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Submit for Admin Approval
              </Button>
            )}
            <Button onClick={organize} disabled={busy || status === 'submitted'} variant="outline" data-testid="organizer-rerun-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Re-run AI
            </Button>
            <Button onClick={discard} disabled={busy} variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="organizer-discard-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Discard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequirementOrganizer;
